import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Database, 
  Settings, 
  Copy, 
  Download,
  GitBranch,
  AlertTriangle,
  CheckCircle,
  Info
} from "lucide-react";
import { toast } from "sonner";

const EntityDetailModal = ({ entity, open, onOpenChange, sources, targets }) => {
  if (!entity) return null;

  const getEntityName = (entity) => {
    if (entity.sourceField && entity.sourceField.length > 0) {
      return entity.sourceField[0].entity || 'N/A';
    }
    return entity.entity || 'N/A';
  };

  const getTargetNameById = (targetId) => {
    if (!targets) return `ID: ${targetId}`;
    const target = targets.find(t => t.id === targetId);
    return target ? `${target.name} (ID: ${targetId})` : `ID: ${targetId}`;
  };

  const getSourceNameById = (sourceId) => {
    if (!sources) return `ID: ${sourceId}`;
    const source = sources.find(s => s.id === sourceId);
    return source ? `${source.provider || source.source} (ID: ${sourceId})` : `ID: ${sourceId}`;
  };

  const copyToClipboard = async (data) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("File downloaded successfully!");
  };

  const renderJsonPreview = (data, title) => {
    if (!data) return null;

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 border rounded p-3 max-h-64 overflow-auto">
            <pre className="text-xs text-slate-700 whitespace-pre-wrap">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
          <div className="flex space-x-2 mt-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => copyToClipboard(data)}
            >
              <Copy className="w-3 h-3 mr-1" />
              Copy
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => downloadJson(data, `${title.toLowerCase().replace(' ', '_')}_${entity.id}`)}
            >
              <Download className="w-3 h-3 mr-1" />
              Download
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white" style={{backgroundColor: 'white', color: '#0f172a'}}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2" style={{color: '#0f172a'}}>
            <Database className="w-5 h-5" />
            <span>{entity.source} → {entity.target}</span>
          </DialogTitle>
          <DialogDescription style={{color: '#64748b'}}>
            Entity: {getEntityName(entity)} | ID: {entity.id}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="dependencies">Dependencies</TabsTrigger>
            <TabsTrigger value="raw">Raw Data</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm" style={{color: '#0f172a'}}>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Source:</span>
                    <Badge variant="outline">{entity.source}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Target:</span>
                    <Badge variant="outline">{entity.target}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Entity:</span>
                    <Badge className="badge-info">{getEntityName(entity)}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Migration Object ID:</span>
                    <span className="text-sm font-mono" style={{color: '#0f172a'}}>{entity.migrationObject || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Main Target ID:</span>
                    <span className="text-sm font-mono" style={{color: '#0f172a'}}>{entity.mainTarget || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm" style={{color: '#0f172a'}}>Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Delimiter Enabled:</span>
                    {entity.isDelimiterEnabled ? 
                      <CheckCircle className="w-4 h-4 text-green-600" /> : 
                      <span className="text-slate-400">No</span>
                    }
                  </div>
                  {entity.delimiter && (
                    <div className="flex justify-between">
                      <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Delimiter:</span>
                      <span className="text-sm font-mono bg-slate-100 px-1 rounded">"{entity.delimiter}"</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Multiple Entities:</span>
                    {entity.selectMultipleEntities ? 
                      <CheckCircle className="w-4 h-4 text-green-600" /> : 
                      <span className="text-slate-400">No</span>
                    }
                  </div>
                  {entity.customFieldKey && (
                    <div className="flex justify-between">
                      <span className="text-sm" style={{color: '#64748b', fontWeight: '500'}}>Custom Field Key:</span>
                      <span className="text-sm font-mono" style={{color: '#0f172a'}}>{entity.customFieldKey}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Alerts */}
            {entity.incompatible && entity.incompatible.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">Incompatible Items</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {entity.incompatible.map((item, index) => (
                      <Badge key={index} className="badge-error text-xs">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="fields" className="space-y-4">
            {entity.sourceField && renderJsonPreview(entity.sourceField, "Source Field Configuration")}
            {entity.targetField && renderJsonPreview(entity.targetField, "Target Field Configuration")}
          </TabsContent>

          <TabsContent value="dependencies" className="space-y-4">
            {entity.dependentTargets && entity.dependentTargets.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center space-x-2">
                    <GitBranch className="w-4 h-4" />
                    <span>Dependent Targets</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {entity.dependentTargets.map((depId, index) => (
                      <div key={index} className="p-2 bg-slate-50 rounded border">
                        <Badge className="badge-warning">
                          {getTargetNameById(depId)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {entity.mappingTargets && entity.mappingTargets.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>Mapping Targets</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {entity.mappingTargets.map((mapId, index) => (
                      <div key={index} className="p-2 bg-slate-50 rounded border">
                        <Badge className="badge-info">
                          {getTargetNameById(mapId)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <h4 className="font-medium mb-2" style={{color: '#0f172a'}}>Migration Source</h4>
              {entity.migrationObject ? (
                <div className="p-2 bg-blue-50 rounded border">
                  <Badge className="badge-info">
                    {getSourceNameById(entity.migrationObject)}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No migration source assigned</p>
              )}
            </div>

            <div>
              <h4 className="font-medium mb-2" style={{color: '#0f172a'}}>Main Target</h4>
              {entity.mainTarget ? (
                <div className="p-2 bg-green-50 rounded border">
                  <Badge className="badge-success">
                    {getTargetNameById(entity.mainTarget)}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No main target assigned</p>
              )}
            </div>

            {entity.sourceExtender && renderJsonPreview(entity.sourceExtender, "Source Extender Configuration")}
          </TabsContent>

          <TabsContent value="raw" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Complete Entity Data</span>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(entity)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy All
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => downloadJson(entity, `entity_${entity.id}_complete`)}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download All
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 border rounded p-3 max-h-80 overflow-auto">
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap">
                    {JSON.stringify(entity, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EntityDetailModal;